import { ActionModal, FormError } from '@kcftech/design-system';
import {
    IAnalyticsModelResponse,
    ICorporationResponse,
    ILocationResponse,
    IModelAssignmentResponse,
    ModelAssignmentCreateRequest,
    SetPropertyChild,
} from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import {
    AnalyticsModelAssignmentForm,
    AnalyticsModelAssignmentFormData,
} from 'components/analytics/AnalyticsAssignmentForm';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { useForm } from 'react-hook-form';

const buildModelProperties = (
    model: IAnalyticsModelResponse,
    parameters: AnalyticsModelAssignmentFormData['parameters'],
) =>
    model.Properties.map<SetPropertyChild>(
        (property) =>
            new SetPropertyChild({
                PropertyId: property.PropertyId,
                Value: parameters[`${property.PropertyId}`]?.trim() ?? '',
            }),
    ).filter((propertyChild) => propertyChild.Value.length > 0);

export type AnalyticsModelAssignmentModalProps = {
    corporation?: ICorporationResponse;
    location?: ILocationResponse;
    onClose: (result?: IModelAssignmentResponse) => void;
};

export function AnalyticsModelAssignmentModal({
    corporation,
    location,
    onClose,
}: AnalyticsModelAssignmentModalProps) {
    const { showToast } = useNotificationContext();

    const form = useForm<AnalyticsModelAssignmentFormData>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        shouldUnregister: true,
        defaultValues: {
            parameters: {},
        },
    });

    const {
        formState: { isSubmitted, isSubmitting, isValid },
        handleSubmit,
    } = form;

    const {
        mutate: saveAssignment,
        isPending: isSaving,
        isError: isSaveError,
    } = useMutation({
        mutationFn: ({
            assignmentType,
            model,
            asset,
            monitoringPoint,
            parameters,
        }: AnalyticsModelAssignmentFormData) => {
            if (!assignmentType) {
                throw new Error('Missing assignmentType');
            }
            if (!model) {
                throw new Error('Missing model');
            }
            const groupId = assignmentType === 'Asset' ? asset?.Id : monitoringPoint?.Id;
            if (!groupId) {
                throw new Error('Missing group id');
            }

            const request = new ModelAssignmentCreateRequest({
                ModelId: model.Id,
                ParentGroupId: groupId,
                AssignedGroupIds: [groupId],
                Properties: buildModelProperties(model, parameters),
            });

            return sdClient.createAnalyticsAssignment({ body: request });
        },

        onSuccess: () => {
            onClose();
            showToast({
                type: 'success',
                content: 'Successfully created model assignment',
            });
        },

        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to create model assignment',
            }),
    });

    const onSubmit = handleSubmit((data) => saveAssignment(data));

    return (
        <ActionModal
            title="Assign Analytics Model"
            actionButtonText="Assign"
            closeButtonText="Cancel"
            height={750}
            width={1300}
            contentNoPadding
            onClose={onClose}
            onAction={onSubmit}
            actionable={!isSubmitting && !isSaving && !(isSubmitted && !isValid)}
            footerContent={
                isSaveError ? (
                    <FormError message="Unable to assign analytics model." />
                ) : undefined
            }
        >
            <AnalyticsModelAssignmentForm
                corporation={corporation}
                location={location}
                form={form}
            />
        </ActionModal>
    );
}
